import React from 'react';
import Dialog from '../Dialog/Dialog';
import DialogTitle from '../Dialog/DialogTitle.styled';
import DialogContent from '@material-ui/core/DialogContent';
import LockOutlined from '@mui/icons-material/LockOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import { Box, Typography } from '@ublend-npm/aulaui-core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  useGetAllPermissions,
  Permission,
} from '@core/permissions/api/getAllPermissions';
import {
  ModalDescription,
  PermissionItem,
  PermissionLabel,
  PermissionModule,
  PermissionsList,
  ErrorMessage,
} from './PermissionsModal.styled';

interface PermissionsModalProps {
  open: boolean;
  onClose: () => void;
  roleName?: string | null;
  userPermissions: string[];
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({
  open,
  onClose,
  roleName,
  userPermissions,
}) => {
  // Fetch all permissions from API
  // Only fetch when modal is open and handle errors gracefully
  const {
    data: permissionsData,
    isLoading,
    error,
  } = useGetAllPermissions(open); // Only fetch when modal is open

  const hasPermission = (permissionKey: string): boolean => {
    return userPermissions.includes(permissionKey);
  };

  // Group permissions by module
  const permissionsByModule = React.useMemo<
    Record<string, Permission[]>
  >(() => {
    const permissions =
      permissionsData?.result?.permissions || permissionsData?.permissions;
    if (!permissions) return {};

    return permissions.reduce((acc, permission) => {
      if (!acc[permission.module]) {
        acc[permission.module] = [];
      }
      acc[permission.module].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);
  }, [permissionsData]);

  return (
    <Dialog id="permissions-modal" open={open} onClose={onClose} maxWidth="sm">
      <DialogTitle onClose={onClose}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ fontSize: '16px', fontWeight: 600 }}
        >
          <LockOutlined style={{ fontSize: '20px' }} />
          Limited Permissions
        </Box>
      </DialogTitle>
      <DialogContent>
        <ModalDescription>
          You currently have limited access in this space. Some actions may be
          restricted based on your role.
        </ModalDescription>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={40} />
          </Box>
        )}

        {error && (
          <ErrorMessage>
            Failed to load permissions. Please try again later.
          </ErrorMessage>
        )}

        {!isLoading && !error && Object.keys(permissionsByModule).length > 0 && (
          <PermissionsList>
            {Object.entries(permissionsByModule).map(
              ([module, permissions]) => (
                <Box key={module} mb={3}>
                  <PermissionModule>{module}</PermissionModule>
                  {permissions.map((permission) => {
                    const hasAccess = hasPermission(permission.key);
                    return (
                      <PermissionItem key={permission.key}>
                        {hasAccess ? (
                          <CheckCircle
                            style={{ color: '#4caf50', fontSize: '20px' }}
                          />
                        ) : (
                          <Cancel
                            style={{ color: '#f44336', fontSize: '20px' }}
                          />
                        )}
                        <PermissionLabel>{permission.label}</PermissionLabel>
                      </PermissionItem>
                    );
                  })}
                </Box>
              )
            )}
          </PermissionsList>
        )}

        {!isLoading && !error && Object.keys(permissionsByModule).length === 0 && (
          <Box py={2}>
            <Typography color="text.secondary">
              No permissions available.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PermissionsModal;
